// src/lib/performance.ts
// Performance monitoring and optimization utilities

export interface PerformanceMetrics {
  cacheHits: number;
  cacheMisses: number;
  totalOperations: number;
  averageResponseTime: number;
  slowestOperation: number;
  fastestOperation: number;
}

class PerformanceMonitor {
  private metrics: PerformanceMetrics = {
    cacheHits: 0,
    cacheMisses: 0,
    totalOperations: 0,
    averageResponseTime: 0,
    slowestOperation: 0,
    fastestOperation: Infinity,
  };

  private operationTimes: number[] = [];

  startTimer(): () => void {
    const startTime = performance.now();

    return () => {
      const endTime = performance.now();
      const duration = endTime - startTime;

      this.recordOperation(duration);
    };
  }

  recordCacheHit(): void {
    this.metrics.cacheHits++;
    this.metrics.totalOperations++;
  }

  recordCacheMiss(): void {
    this.metrics.cacheMisses++;
    this.metrics.totalOperations++;
  }

  private recordOperation(duration: number): void {
    this.operationTimes.push(duration);
    this.metrics.totalOperations++;

    // Update statistics
    if (duration > this.metrics.slowestOperation) {
      this.metrics.slowestOperation = duration;
    }
    if (duration < this.metrics.fastestOperation) {
      this.metrics.fastestOperation = duration;
    }

    // Calculate running average
    const totalTime = this.operationTimes.reduce((sum, time) => sum + time, 0);
    this.metrics.averageResponseTime = totalTime / this.operationTimes.length;
  }

  getMetrics(): PerformanceMetrics {
    return { ...this.metrics };
  }

  getCacheHitRate(): number {
    if (this.metrics.totalOperations === 0) return 0;
    return (this.metrics.cacheHits / this.metrics.totalOperations) * 100;
  }

  reset(): void {
    this.metrics = {
      cacheHits: 0,
      cacheMisses: 0,
      totalOperations: 0,
      averageResponseTime: 0,
      slowestOperation: 0,
      fastestOperation: Infinity,
    };
    this.operationTimes = [];
  }

  logMetrics(label: string = 'Performance Metrics'): void {
    console.group(`📊 ${label}`);
    console.log(`Cache Hit Rate: ${this.getCacheHitRate().toFixed(2)}%`);
    console.log(`Total Operations: ${this.metrics.totalOperations}`);
    console.log(`Cache Hits: ${this.metrics.cacheHits}`);
    console.log(`Cache Misses: ${this.metrics.cacheMisses}`);
    console.log(`Average Response Time: ${this.metrics.averageResponseTime.toFixed(2)}ms`);
    console.log(`Fastest Operation: ${this.metrics.fastestOperation.toFixed(2)}ms`);
    console.log(`Slowest Operation: ${this.metrics.slowestOperation.toFixed(2)}ms`);
    console.groupEnd();
  }
}

// Global performance monitor instance
export const performanceMonitor = new PerformanceMonitor();

// Utility function to measure execution time
export function measureExecution<T>(
  fn: () => T | Promise<T>,
  label?: string
): T | Promise<T> {
  const endTimer = performanceMonitor.startTimer();

  try {
    const result = fn();
    if (result instanceof Promise) {
      return result.finally(endTimer);
    } else {
      endTimer();
      return result;
    }
  } catch (error) {
    endTimer();
    throw error;
  }
}

// Cache performance decorator
export function withCachePerformance<T extends (...args: any[]) => any>(
  fn: T,
  cacheKey: string,
  cache: { get: (key: string) => any; set: (key: string, value: any) => void }
): T {
  return ((...args: any[]) => {
    const key = `${cacheKey}:${JSON.stringify(args)}`;

    const cached = cache.get(key);
    if (cached !== undefined) {
      performanceMonitor.recordCacheHit();
      return cached;
    }

    performanceMonitor.recordCacheMiss();
    const result = fn(...args);
    if (result instanceof Promise) {
      return result.then(value => {
        cache.set(key, value);
        return value;
      });
    } else {
      cache.set(key, result);
      return result;
    }
  }) as T;
}

// Memory usage monitoring
export function getMemoryUsage(): { used: number; total: number; percentage: number } {
  if (typeof performance !== 'undefined' && (performance as any).memory) {
    const memory = (performance as any).memory;
    return {
      used: memory.usedJSHeapSize,
      total: memory.totalJSHeapSize,
      percentage: (memory.usedJSHeapSize / memory.totalJSHeapSize) * 100,
    };
  }
  return { used: 0, total: 0, percentage: 0 };
}

// Performance reporting utility
export function reportPerformance(): void {
  const metrics = performanceMonitor.getMetrics();
  const memory = getMemoryUsage();

  console.group('🚀 Application Performance Report');
  performanceMonitor.logMetrics();

  if (memory.used > 0) {
    console.log(`Memory Usage: ${(memory.used / 1024 / 1024).toFixed(2)}MB / ${(memory.total / 1024 / 1024).toFixed(2)}MB (${memory.percentage.toFixed(2)}%)`);
  }

  console.groupEnd();
}

// Development-only performance monitoring
export function enablePerformanceMonitoring(): void {
  if (process.env.NODE_ENV === 'development') {
    // Log metrics every 30 seconds in development
    setInterval(() => {
      reportPerformance();
    }, 30000);
  }
}
